/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import type {BlockNavigationOptions} from '@adobe/exc-app/page';
import {history} from '@exc/router';
import {isOrgOrSubOrgChange} from '../utils';
import {UnregisterCallback} from 'history';

export class BlockNavigationService {
  private unblock: UnregisterCallback | undefined;
  private unloadPromptMessage: string;

  constructor(unloadPromptMessage: string) {
    this.unloadPromptMessage = unloadPromptMessage;
  }

  /**
   * Sets the unload message to show upon navigation.
   * @param message - Message to show
   */
  public set unloadMessage(message: string) {
    this.unloadPromptMessage = message;
  }

  /**
   * Handler for before unload event
   * @param e - Before Unload event
   */
  private unloadListener = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = this.unloadPromptMessage;
  };

  /**
   * Block/Unblock navigation
   * @param enabled - True to block, false to unblock
   * @param options - Additional options
   */
  public blockNavigation(enabled: boolean, options?: BlockNavigationOptions) {
    if (!enabled) {
      window.removeEventListener('beforeunload', this.unloadListener);
      this.unblock && this.unblock();
      return;
    }

    this.unblock = history.block((location, action) => {
      if (action !== 'REPLACE') {
        const promptUser = (options?.onOrgChangeOnly) ?
          isOrgOrSubOrgChange(history.location.pathname, location.pathname) : true;
        if (promptUser) {
          return this.unloadPromptMessage;
        }
      }
    });
    window.addEventListener('beforeunload', this.unloadListener);
  }
}
